import CloseIcon from '@mui/icons-material/Close';
import DeleteButton from './DeleteButton';
import DeleteIconButton from './DeleteIconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Checkbox, Chip, CircularProgress, Grid, IconButton, SvgIcon, TextField, Tooltip, Typography } from '@mui/material';
import { blueGrey, grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import arrayMove from 'array-move';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import Gallery from 'react-photo-gallery';
import { useHistory } from 'react-router';
import { SortableContainer, SortableElement, sortableHandle } from 'react-sortable-hoc';

import { DataAPIContext, GET_MANY_REFERENCE } from '../api';
import { DELETE, UPDATE, UPDATE_BATCH, UPLOAD } from '../api/dataProvider';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { ContextStore } from '../store/contextStore';
import { checkAndAddHttpPrefix } from '../utils/http_prefix';
import validURL from '../utils/validators/url';
import FixedLinearProgress from './FixedLinearProgress';
import ImageActionButton from './ImageActionButton';
import images from './images';

const Checkmark = () => {
    return (
        <SvgIcon viewBox="0 0 32 32" fill="none" width="32" height="32">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M16 31C24.2843 31 31 24.2843 31 16C31 7.71573 24.2843 1 16 1C7.71573 1 1 7.71573 1 16C1 24.2843 7.71573 31 16 31Z" fill="#007AFF" stroke="white" stroke-width="2" />
            <path d="M7.81836 16.6819L12.5911 21.4546L23.5002 10.5455" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="#007AFF" />
        </SvgIcon>
    );
};

const useStyles = makeStyles((theme) => ({
    gallery: {
        // height: "600px",
        overflowY: 'hidden',
    },
    root: {
        position: 'relative',
        marginBottom: theme.spacing(4),
        padding: '0.5rem',
    },
    unSelectedRoot: {
        position: 'relative',
        width: 250,
        height: 220,
        borderRadius: '20px',
    },
    selectedRoot: {
        backgroundColor: grey[200],
        position: 'relative',
        width: 250,
        height: 220,
        borderRadius: '20px',
    },
    uploadingRoot: {
        backgroundColor: blueGrey[400],
        borderRadius: '20px',
        opacity: 0.5,
    },
    chip: {
        position: 'absolute',
        color: "#fff !important",
        backgroundColor: "black !important",
        left: theme.spacing(2),
        top: theme.spacing(2),
    },

    checkbox: {
        // +8px: the IconButton that used to wrap the Checkbox added 8px of padding on each side.
        position: 'absolute',
        left: '126px',
        bottom: `calc(${theme.spacing(14.5)} + 8px)`,
        color: '#ffffff',
        cursor: 'pointer',
    },
    editIcon: {
        position: 'absolute',
        left: '208px',
        bottom: theme.spacing(10.5),
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
            left: 'calc(100vw - 5rem) !important',
        },
    },

    dragIndicator: {
        position: 'absolute',
        left: '167px',
        bottom: theme.spacing(6.5),
        cursor: 'grab',
        [theme.breakpoints.down('sm')]: {
            left: 'calc(100vw - 7.6rem) !important',
        },
    },
    image: {
        width: 200,
        height: 200,
        borderRadius: '20px',
        margin: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            width: 'calc(100vw - 5.5rem) !important',
            height: '100%',
        },
    },
    uploadingImage: {
        opacity: 0.7,
    },
    detailsImage: {
        borderRadius: '20px',
        height: '300px !important',
        width: '300px',
        backgroundSize: 'cover',
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        cursor: 'pointer',
    },
    uploadProgressIcon: {
        position: 'absolute',
        left: 70,
        top: 70,
    },
}));

const TYPE_IMAGE = 'image';
const TYPE_UPLOADING = 'uploading';
const DOWNLOAD_ERROR = 4; // demo.models.DOWNLOAD_ERROR
// Mirrors the backend's GALLERY upload policy (demo/lib/image_upload_policy.py): raster only.
// SVG is accepted for activity category icons alone; a gallery refuses it with a 400.
// AVIF is accepted and stored as a JPEG (a PNG when transparent): the resize front cannot show AVIF (CP-7133).
const ACCEPTED_IMAGE_UPLOAD = '.jpg,.jpeg,.jpe,.jfif,.png,.gif,.webp,.avif,image/jpeg,image/png,image/gif,image/webp,image/avif';
const REJECTED_IMAGE_MESSAGE = 'Images must be JPG, PNG, GIF, WebP, or AVIF.';

const getComparableState = (image) => {
    const i = { ...image };
    return JSON.stringify(i);
};

const ImageDetails = (props) => {
    const { settings } = React.useContext(ContextStore);
    const classes = useStyles();
    const imgPreset = `cover_350x200`;
    // const captionRef = React.useRef(null);
    // const videoUrlRef = React.useRef(null);
    const [image, setImage] = React.useState(props.image);
    const [originalState] = React.useState(getComparableState(props.image));
    // const [caption, setCaption] = React.useState(props.image.caption);
    // const [videoUrl, setVideoUrl] = React.useState(props.image.video_url);
    // const [file, setFile] = React.useState(null);
    console.log('ImageDetails', settings.images.base, image.image, imgPreset, '|', image, props);
    const src = !!image.file ? URL.createObjectURL(image.file) : images.url(settings.images.base, image.image, imgPreset);
    const syncActive = props.image.type === TYPE_UPLOADING;

    const currentState = getComparableState(image);
    const hasChanged = originalState !== currentState;
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [VideoUrlErrorState, setVideoUrlErrorState] = React.useState(false);

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            console.log('drop', acceptedFiles[0], props.onSelect);
            setImage((ps) => ({ ...ps, file: acceptedFiles[0] }));
            // setFile(acceptedFiles[0]);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [props.file]
    );

    const onStringChange = (ev) => {
        setImage((ps) => ({ ...ps, [ev.target.name]: ev.target.value }));
    };

    const onKeyPress = (ev) => {
        if (ev.keyCode === 13) {
            onImageSave();
        }
    };

    const onRootKeyPress = (ev) => {
        if (ev.keyCode === 27) {
            props.onClose && props.onClose();
        }
    };

    // const onClearFile = () => {
    //     props.onSelect && props.onSelect(null, props.file);
    // }

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
        accept: ACCEPTED_IMAGE_UPLOAD,
    });

    const onImageSave = (ev) => {
        // const update = Object.assign({}, props.image);
        // update.caption = caption;
        // update.video_url = videoUrl;
        // if (!!file) {
        //     update.file = file;
        // }
        // // console.log('image save', props.image, update);
        image.video_url = checkAndAddHttpPrefix(image.video_url);
        props.onChange && props.onChange(image);
    };

    const onImageClick = (ev) => {
        const origSrc = images.url(settings.images.base, props.image.image);
        window.open(origSrc);
    };

    const onImageDelete = (ev) => {
        setConfirmationDialogAction('delete image');
    };

    const handleVideoURLOnBlur = () => {
        if (validURL(image.video_url)) {
            setVideoUrlErrorState(false);
        } else {
            setVideoUrlErrorState(true);
        }
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        if (confirmationDialogAction) {
            props.onDelete && props.onDelete(props.image);
        }
        confirmationDialogCancel();
    };

    return (
        <div onKeyUp={onRootKeyPress}>
            {confirmationDialogAction ? (
                <ConfirmationDialog classes={{ paper: classes.paper }} keepMounted onCancel={confirmationDialogCancel} onConfirm={confirmationDialogConfirm} title={'Please confirm'} okButtonText={'Delete'} cancelButtonText={'Cancel'} open={!!confirmationDialogAction}>
                    {'Do you really want to delete this image?'}
                </ConfirmationDialog>
            ) : null}
            <Grid container direction="row" spacing={2}>
                <Grid item xs={6} container alignItems="center">
                    <Typography variant="h6">Image Details</Typography>
                </Grid>
                <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
                    <Tooltip title="Save changes to image">
                        <span>
                            <IconButton disabled={!hasChanged || syncActive || VideoUrlErrorState || !props.hasEditPermission} color="primary" variant="contained" size="small" onClick={onImageSave}>
                                <SaveIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton aria-label="close-preview" onClick={props.onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>
                {/* CP-5556: trash on the image's top-right corner, [REPLACE IMAGE] on its bottom-right, as on Edit Bus. */}
                <Grid item xs={12} className={classes.detailsImage} style={{ padding: 0, position: 'relative' }}>
                    <a href={images.url(settings.images.base, props.image.image)}>
                        <div className={classes.detailsImage} style={{ backgroundImage: `url(${src})`, width: '100%', height: '100%', margin: 0 }} title="Click to open image in a new tab" onClick={onImageClick} />
                    </a>
                    <span style={{ position: 'absolute', top: 11, right: 11 }}>
                        <DeleteIconButton title="Delete image" overImage allowed={props.hasDeletePermission && props.hasEditPermission} disabled={syncActive} onClick={onImageDelete} />
                    </span>
                    <input {...dz.getInputProps()} />
                    <ImageActionButton hasImage overImage disabled={!props.hasEditPermission} onClick={dz.open} sx={{ position: 'absolute', right: 12, bottom: 12 }} />
                </Grid>
                <Grid item xs={12}>
                    <Box mt={2} ml={0}>
                        <TextField
                            variant="filled"
                            size="small"
                            label="Caption Text (optional)"
                            name="caption"
                            fullWidth
                            value={image.caption}
                            onChange={onStringChange}
                            onKeyUp={onKeyPress}
                            autoFocus
                            inputProps={{
                                maxLength: 100,
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box mb={4}>
                        <TextField
                            variant="filled"
                            size="small"
                            label="Video URL (optional)"
                            name="video_url"
                            fullWidth
                            value={image.video_url}
                            onChange={onStringChange}
                            onKeyUp={onKeyPress}
                            onBlur={handleVideoURLOnBlur}
                            error={VideoUrlErrorState}
                            helperText={VideoUrlErrorState && 'Please enter a valid URL.'}
                        />
                    </Box>
                </Grid>
                {/* <Grid item xs={6}>
                <Button disabled={syncActive} color="primary" variant="contained" size="small" onClick={onImageSave}><SaveIcon/>&nbsp;Save</Button>
            </Grid>
            <Grid item xs={6} container justify="flex-end">
                <Button disabled={syncActive} color="secondary" size="small" onClick={onImageDelete}><DeleteIcon fontSize="small" />&nbsp;Delete</Button>
            </Grid> */}
            </Grid>
        </div>
    );
};

// The Tooltip sits inside the handle: sortableHandle finds its node with findDOMNode,
// which resolves through the Tooltip to the IconButton.
const ImageDragHandle = sortableHandle((props) => (
    <Tooltip title="Drag to reorder">
        <IconButton color="primary" {...props}>
            <DragIndicatorIcon fontSize="small" />
        </IconButton>
    </Tooltip>
));

const GalleryUploadingImage = (props) => {
    const classes = useStyles();
    return (
        <Box position="relative" display="inline-flex">
            <img src={props.src} alt="img" className={clsx(classes.image, classes.uploadingImage)} />
            <Box top={0} left={0} bottom={0} right={0} position="absolute" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress variant="indeterminate" />
            </Box>
        </Box>
    );
};

const GalleryImage = (props) => {
    const classes = useStyles();
    // A failed server download has no image, so its <img> never fires onLoad or onError:
    // only spin while there is something to load (CP-8371).
    const downloadFailed = +props.imageProps.download_status === DOWNLOAD_ERROR;
    const [loading, setLoading] = React.useState(!!props.imageProps.src && !downloadFailed);

    return (
        <div className={clsx(classes.root, props.isActive ? classes.selectedRoot : classes.unSelectedRoot)}>
            {loading && <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
            </div>}
            {downloadFailed && (
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                    <Chip color="error" size="small" label="Download failed" title={props.imageProps.download_error || ''} />
                </div>
            )}
            {props.showSelectBt && props.hasEditPermission ? (
                <IconButton className={props.isSelected ? classes.unselectBt : classes.selectBt} onClick={props.onSelectBtClick}>
                    {props.isSelected ? <Checkmark color="primary" /> : <RadioButtonUncheckedOutlinedIcon />}
                </IconButton>
            ) : null}
            {props.chip ? <Chip className={classes.chip} label={props.chip} /> : null}
            <img {...props.imageProps} alt="img" onLoad={() => setLoading(false)} onError={() => setLoading(false)} className={classes.image} />
            <Tooltip title="Edit image">
                <IconButton className={classes.editIcon} color="primary" onClick={props.onClick}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <ImageDragHandle className={classes.dragIndicator} />
            {!props.isSmallScreen && props.hasEditPermission && props.hasDeletePermission && (
                <Tooltip title="Select image">
                    {/* The Checkbox is the control itself: wrapped in an IconButton, clicks on the button's
                        padding did nothing and MUI warned about the nested onClick. */}
                    <Checkbox
                        className={classes.checkbox}
                        checked={props.imagesToDelete.filter((img_id) => img_id === props.imageProps.id).length > 0}
                        onChange={() => props.setImagesToDelete(props.imageProps)}
                        inputProps={{ 'aria-label': 'Select image' }}
                    />
                </Tooltip>
            )}
        </div>
    );
};

const GalleryItem = (props) => {
    // { index, onClick, photo, margin, direction, top, left, selected }
    const [isSelected, setIsSelected] = React.useState(props.selected);
    // const imgStyle = { margin: margin };
    // if (direction === "column") {
    //     imgStyle.position = "absolute";
    //     imgStyle.left = left;
    //     imgStyle.top = top;
    // }
    const handleSelectBtClick = (ev) => {
        const newIsSelected = !isSelected;
        setIsSelected(newIsSelected);
        props.onSelect && props.onSelect(props, newIsSelected);
    };

    const handleClick = (ev) => {
        console.log('photo click', ev, props);
        props.onClick && props.onClick(props, ev);
    };

    const chip = props.idx === 0 ? 'Cover' : null;
    const showSelectBt = props.selectable;

    const { onClick: defOnClick, ...photoProps } = props.photo;

    const isActive = props.activeImage && props.activeImage.id === photoProps.id;

    // console.log('Photo', isActive, props);

    if (photoProps.type === TYPE_IMAGE) {
        return (
            <GalleryImage
                imagesToDelete={props.imagesToDelete}
                setImagesToDelete={props.setImagesToDelete}
                isActive={isActive}
                showSelectBt={showSelectBt}
                isSelected={isSelected}
                onSelectBtClick={handleSelectBtClick}
                onClick={handleClick}
                chip={chip}
                imageProps={photoProps}
                isSmallScreen={props.isSmallScreen}
                hasEditPermission={props.hasEditPermission}
                hasDeletePermission={props.hasDeletePermission}
            />
        );
    } else if (photoProps.type === TYPE_UPLOADING) {
        return <GalleryUploadingImage {...photoProps} />;
    }
};

const SortableImage = SortableElement((item) => {
    return <GalleryItem {...item} />;
});
const SortableGallery = SortableContainer(({ items, onImageClick, onSelect, activeImage, isSmallScreen, imagesToDelete, setImagesToDelete, hasEditPermission, hasDeletePermission }) => (
    <Gallery
        photos={items}
        renderImage={(props) => {
            return <SortableImage idx={props.index} {...props} hasEditPermission={hasEditPermission} hasDeletePermission={hasDeletePermission} imagesToDelete={imagesToDelete} setImagesToDelete={setImagesToDelete} activeImage={activeImage} onClick={onImageClick} onSelect={onSelect} isSmallScreen={isSmallScreen} />;
        }}
    />
));

// const debugGallery = (prefix, gallery) => {
//     console.log(`gallery:${prefix}:`, gallery.map((i, idx) => `${idx}-${i.sequence}[${i.id}]`).join(', ') );
// }

const apiImage2internal = (aimg, settings, imgPreset) => {
    const inProgress = [2, 3].indexOf(+aimg.download_status) >= 0;
    const src = inProgress ? aimg.source_url : images.url(settings.images.base, aimg.image, imgPreset);
    return {
        ...aimg,
        src,
        type: inProgress ? TYPE_UPLOADING : TYPE_IMAGE,
        width: 1,
        height: 1,
        caption: aimg.caption || '',
        video_url: aimg.video_url || '',
    };
};

const ImageGalleryEditor = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    // const urlParams = useParams();
    // const location = useLocation();
    const classes = useStyles();
    const { settings } = React.useContext(ContextStore);

    const cellHeight = +props.cellHeight || 175;
    const imgPreset = `cover_${cellHeight}x${cellHeight}`;
    const [activeImage, setActiveImage] = React.useState(null);
    const mounted = React.useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const [isServerUploading, setIsServerUploading] = React.useState((props.list || []).filter((x) => [2, 3].indexOf(+x.download_status) >= 0).length > 0);
    const [imagesToDelete, setImagesToDelete] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [gallery, setGallery] = React.useState((props.list || []).map((img, index) => apiImage2internal(img, settings, imgPreset)));
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [myFiles, setMyFiles] = React.useState([]);
    const cols = props.isSmallScreen ? (activeImage ? 0 : 12) : activeImage ? 8 : 12;
    const hasDeletePermission = props.hasDeletePermission !== undefined ? props.hasDeletePermission : true;

    React.useEffect(() => {
        if (props.imageId && props.navigationPrefix) {
            const uimg = gallery.filter((x) => +x.id === +props.imageId);
            if (uimg.length === 1) {
                setActiveImage(uimg[0]);
                return;
            }
        } else if (props.navigationPrefix) {
            setActiveImage(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.imageId, props.navigationPrefix, props.list, activeImage]);

    const navigateToImage = (img) => {
        if (props.navigationPrefix) {
            const url = img ? `${props.navigationPrefix}/${img.id}` : props.navigationPrefix;
            history.push(url);
        } else {
            setActiveImage(img);
        }
    };

    React.useEffect(() => {
        if (!isServerUploading) {
            return;
        }
        
        let tm = null;

        const checkImagesStatus = () => {
            const params = {
                filter: props.resourceMeta,
                pagination: {
                    page: 1,
                    perPage: 100000,
                },
                sort: { field: 'sequence', order: 'ASC' },
            };

            props.setStillDownloadingImages && props.setStillDownloadingImages(true);

            dataAPI(GET_MANY_REFERENCE, props.resource, params).then((res) => {
                const isUploading = (res.data || []).filter((x) => [2, 3].indexOf(+x.download_status) >= 0).length > 0;
                console.log('image status update', res.statusCode, res.data, isUploading, !!mounted.current);
                if (!mounted.current) {
                    return;
                }
                setGallery((res.data || []).map((x) => apiImage2internal(x, settings, imgPreset)));
                setIsServerUploading(isUploading);
                if (isUploading) {
                    console.log('schedule next image status update in 5 s...');
                    tm = setTimeout(checkImagesStatus, 5000);
                } else {
                    props.setStillDownloadingImages && props.setStillDownloadingImages(false);
                }
            });
        };
        checkImagesStatus();
        return () => tm && clearTimeout(tm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.list, isServerUploading]);

    // { getRootProps, getInputProps, open: openUploadDialog, acceptedFiles }

    const onDrop = (acceptedFiles) => {
        console.log('onDrop', acceptedFiles);
        setMyFiles([...acceptedFiles]);
    };

    // const removeFile = (file) => {
    //     const newFiles = [...myFiles];
    //     newFiles.splice(newFiles.indexOf(file), 1);
    //     setMyFiles(newFiles);
    // };

    const onDropRejected = (rejections) => {
        if (rejections && rejections.length > 0) {
            enqueueSnackbar(REJECTED_IMAGE_MESSAGE, { variant: 'error' });
        }
    };

    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        onDrop: onDrop,
        onDropRejected: onDropRejected,
        multiple: true,
        accept: ACCEPTED_IMAGE_UPLOAD,
    });

    const handleOnChange = (g) => {
        const external = g
            .filter((i) => i.type === 'image')
            .map((i) => {
                const e = Object.assign({}, i);
                delete e.type;
                delete e.width;
                delete e.height;
                delete e.src; // this src includes image preset used by the gallery, so it is not general enough to be worth returning
                return e;
            });
        props.onChange && props.onChange(external);
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        //Check for more than one image and confirm new index (#CP-2449)
        if (oldIndex !== newIndex && gallery.length > 1) {
            const originalGallery = [...gallery];
            const newGallery = arrayMove(gallery, oldIndex, newIndex);
            setGallery(newGallery);
            handleOnChange(newGallery);
            const params = {
                urlSuffix: 'reorder/',
                data: {
                    images: newGallery.filter((gimg) => !!gimg.id).map((gimg) => gimg.id),
                    ...props.resourceMeta,
                },
            };
            return dataAPI(UPDATE_BATCH, props.resource, params).then((res) => {
                console.log('image reorder', res);
                
                if (res.statusCode >= 400) {
                    // Handle error response
                    console.error('image reorder error', res);
                    // Revert gallery changes on error
                    setGallery(originalGallery);
                    handleOnChange(originalGallery);
                    
                    // Show error message from server response
                    const errorMessage = res.data?.message || 'Failed to save image order';
                    enqueueSnackbar(errorMessage, { variant: 'error' });
                } else {
                    enqueueSnackbar('Image order saved', { variant: 'success' });
                }
            }).catch((error) => {
                console.error('image reorder error', error);
                // Revert gallery changes on error
                setGallery(originalGallery);
                handleOnChange(originalGallery);
                
                // Show error message from server response
                const errorMessage = error.response?.data?.message || 'Failed to save image order';
                enqueueSnackbar(errorMessage, { variant: 'error' });
            });
        }
    };

    const onImageClick = (image) => {
        if (image.photo.type === TYPE_IMAGE) {
            // setActiveImage(image.photo);
            navigateToImage(image.photo);
        }
    };
    const onSelect = (ev) => {
        console.log('onSelect', ev);
    };
    const onDetailsClose = (ev) => {
        // setActiveImage(null);
        navigateToImage(null);
    };

    const onDetailsChange = (img) => {
        // const tmpImg = {...img, type: TYPE_UPLOADING, width: 1, height: 1 }
        // const tmpGallery = gallery.map((i) => (i.id === tmpImg.id ? tmpImg : i ));
        // setGallery(tmpGallery);
        // setActiveImage(tmpImg);
        const params = {
            id: img.id,
            data: {
                caption: img.caption,
                video_url: img.video_url,
                ...props.resourceMeta,
            },
        };
        if (img.file) {
            params.data.file = img.file;
            params.upload = true;
        }
        console.log('image update', img, params);
        return dataAPI(UPDATE, props.resource, params).then((res) => {
            console.log('image res', res);
            const newGallery = gallery.map((i) => (i.id === img.id ? apiImage2internal(res.data, settings, imgPreset) : i));
            setGallery(newGallery);
            // setActiveImage(res.data);
            handleOnChange(newGallery);
            enqueueSnackbar('Image details saved', { variant: 'success' });
            history.push();
        });
    };

    const bulkImageDelete = async () => {
        let new_gallery = [...gallery];
        setLoading(true);
        let deletedCount = 0;
        let errorCount = 0;
        
        for (let index = 0; index < imagesToDelete.length; index++) {
            const result = await dataAPI(DELETE, props.resource, { id: imagesToDelete[index] })
                .then((res) => {
                    if (res.statusCode >= 400) {
                        errorCount++;
                        return false;
                    }
                    deletedCount++;
                    return true;
                })
                .catch((error) => {
                    console.error('Bulk image delete error', error);
                    errorCount++;
                    return false;
                });
            
            if (result) {
                new_gallery = new_gallery.filter((i) => i.id !== imagesToDelete[index]);
            }
        }
        
        setGallery(new_gallery);
        setImagesToDelete([]);
        handleOnChange(new_gallery);
        
        if (deletedCount > 0) {
            enqueueSnackbar(`${deletedCount} image${deletedCount > 1 ? 's' : ''} deleted`, { variant: 'success' });
        }
        if (errorCount > 0) {
            enqueueSnackbar(`Failed to delete ${errorCount} image${errorCount > 1 ? 's' : ''}. You may not have permission.`, { variant: 'error' });
        }
        
        setLoading(false);
    };

    const onImageDelete = async (img, bulk) => {
        setLoading(true)
        const tmpImg = { ...img, type: TYPE_UPLOADING };
        const tmpGallery = gallery.map((i) => (i.id === img.id ? tmpImg : i));
        if (!bulk) {
            setGallery(tmpGallery);
            setActiveImage(tmpImg);
        }

        await dataAPI(DELETE, props.resource, { id: img.id }).then((res) => {
            if (res.statusCode >= 400) {
                const errorMessage = res.data?.message || res.data?.detail || 'You do not have permission to delete images';
                enqueueSnackbar(errorMessage, { variant: 'error' });
                if (!bulk) {
                    setGallery(gallery);
                    setActiveImage(img);
                }
                setLoading(false);
                return;
            }
            if (bulk) {
                navigateToImage(null);
                setLoading(false)
                return;
            }
            const newGallery = gallery.filter((i) => i.id !== img.id);
            setGallery(newGallery);
            // setActiveImage(null);
            navigateToImage(null);
            handleOnChange(newGallery);
            enqueueSnackbar('Image deleted');
        }).catch((error) => {
            console.error('Image delete error', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.detail || 'Failed to delete image';
            enqueueSnackbar(errorMessage, { variant: 'error' });
            if (!bulk) {
                setGallery(gallery);
                setActiveImage(img);
            }
        });
        setLoading(false)
        return;
    };

    const upload_batch = async (files, source) => {
        const data = { source: source, ...props.resourceMeta };
        files.forEach((f, idx) => { data[`file[${idx}]`] = f; });
        const params = {
            data: data,
            urlSuffix: 'upload_batch/',
        };
        console.log('upload_batch', files, params, props.resourceMeta, props);
        setLoading(true);
        const res = await dataAPI(UPLOAD, props.resource, params);
        setLoading(false);
        return res;
    }

    React.useEffect(() => {
        let active = true;
        setLoading(true);

        // const promises = myFiles.map((f) =>
        //     upload({ file: f, source: 'uploaded' }).then((res) => ({
        //         data: res.data,
        //         file: f,
        //     }))
        // );
        const tmpGallery = [...gallery]
            .concat(
                myFiles.map((f) => ({
                    type: TYPE_UPLOADING,
                    file: f,
                    src: URL.createObjectURL(f),
                    caption: f.name,
                }))
            );

        // setMyFiles([]);
        setGallery(tmpGallery);

        if (myFiles.length === 0) { // early return if no files to upload
            setLoading(false);
            return () => {
                active = false;
            };
        }

        upload_batch(myFiles, 'uploaded')
            .then((res) => {
                if (!active) { return; }
                if (res.statusCode >= 400) {
                    setGallery([...gallery]);
                    enqueueSnackbar(res.data?.message || REJECTED_IMAGE_MESSAGE, { variant: 'error' });
                    return;
                }
                const reslist = res.data || [];
                if (reslist.length === 0) { return; }
                console.log('uploaded', reslist, tmpGallery);
                const newGallery = [...gallery]
                    .concat(reslist.map((r, ridx) => {
                        return apiImage2internal(r, settings, imgPreset);
                    }));
                setGallery(newGallery);
                handleOnChange(newGallery);
                if (reslist.length > 0) {
                    enqueueSnackbar(`${reslist.length} image${reslist.length > 1 ? 's' : ''} uploaded`, { variant: 'success' });
                }
            })
            .finally(() => setLoading(false));


        // Promise.all(promises).then((reslist) => {
        //     // TODO: handle case when some (or all) of the requests failed
        //     // console.log('Promise.all', active, reslist);
        //     if (!active || reslist.length === 0) {
        //         return;
        //     }
        //     const getFile = (f) => {
        //         const found = reslist.filter((x) => x.file === f);
        //         return found.length === 1 ? found[0].data : null;
        //     };
        //     // replace upload stubs with real uploaded files
        //     const newGallery = tmpGallery.map((i) => {
        //         const found = getFile(i.file);
        //         // console.log('newGallery map', i.file, found);
        //         return found ? apiImage2internal(found, settings, imgPreset) : i;
        //     });
        //     // console.log('newGallery', newGallery)
        //     setGallery(newGallery);
        //     handleOnChange(newGallery);
        //     if (gallery.length < newGallery.length) {
        //         enqueueSnackbar('Image uploaded', { variant: 'success' });
        //     }
        // }).then(res => setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myFiles]);

    const handleImagesToDelete = (data) => {
        let img_id = data.id;
        setImagesToDelete((prev) => {
            if (prev.includes(img_id)) {
                return prev.filter((id) => id !== img_id);
            }
            return [...prev, img_id];
        });
    };

    const onImagesDelete = (ev) => {
        setConfirmationDialogAction('delete image');
    };
    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        if (confirmationDialogAction) {
            bulkImageDelete();
        }
        confirmationDialogCancel();
    };
    return (
        <>
            {confirmationDialogAction ? (
                <ConfirmationDialog classes={{ paper: classes.paper }} keepMounted onCancel={confirmationDialogCancel} onConfirm={confirmationDialogConfirm} title={'Please confirm'} okButtonText={'Delete'} cancelButtonText={'Cancel'} open={!!confirmationDialogAction}>
                    {imagesToDelete.length === 1 ? 'Do you really want to delete this image?' : 'Do you really want to delete these images?'}
                </ConfirmationDialog>
            ) : null}
            <div className={classes.root}>
                {props.isSmallScreen && activeImage ? null : (
                    <React.Fragment>
                        <Box mb={2}>
                            <Typography variant="body2">You can sort images by simply dragging and dropping them.</Typography>
                        </Box>
                        <Box mb={1} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                            <Tooltip title={props.hasEditPermission ? '' : 'You do not have permission to add new images'}>
                                <span>
                                    <ImageActionButton label="Upload images" size="small" disabled={!props.hasEditPermission} onClick={dz.open} />
                                </span>
                            </Tooltip>
                            {imagesToDelete.length > 0 && hasDeletePermission && (
                                <DeleteButton onClick={onImagesDelete}>
                                    {imagesToDelete.length === 1 ? 'Delete image' : 'Delete images'}
                                </DeleteButton>
                            )}
                        </Box>
                    </React.Fragment>
                )}
                <FixedLinearProgress visibility={isServerUploading || loading} />
                <Grid container direction="row" ref={mounted}>
                    {props.isSmallScreen && activeImage ? null : (
                        <React.Fragment>
                            <Grid item xs={cols} {...dz.getRootProps({ className: 'dropzone' })} className={classes.gallery} style={{ height: `${props.height}px` }}>
                                <input {...dz.getInputProps()} />
                                <SortableGallery
                                    hasEditPermission={props.hasEditPermission}
                                    hasDeletePermission={hasDeletePermission}
                                    items={gallery}
                                    imagesToDelete={imagesToDelete}
                                    setImagesToDelete={handleImagesToDelete}
                                    onSortEnd={onSortEnd}
                                    onSelect={onSelect}
                                    onImageClick={onImageClick}
                                    activeImage={activeImage}
                                    axis={'xy'}
                                    useDragHandle
                                    isSmallScreen={props.isSmallScreen}
                                />
                            </Grid>
                        </React.Fragment>
                    )}
                    {activeImage ? (
                        <Grid item xs={12 - cols}>
                            <ImageDetails hasEditPermission={props.hasEditPermission} hasDeletePermission={hasDeletePermission} key={JSON.stringify(activeImage)} image={activeImage} onChange={onDetailsChange} onDelete={onImageDelete} onClose={onDetailsClose} isSmallScreen={props.isSmallScreen} />
                        </Grid>
                    ) : null}
                </Grid>
            </div>
        </>
    );
};

export default ImageGalleryEditor;
